import React from 'react';
import { PolicyImpactResults } from '../../services/policyImpactService';
import './VehicleElectrificationPolicy.css';

interface VehicleElectrificationPolicyProps {
  currentEVPercent: number; // 2045 BAU EV percentage (slider default/minimum)
  currentEVPercent2023?: number; // 2023 EV percentage (for display)
  targetEVPercent: number; // Target EV percentage by 2045 (from slider)
  location: string; // Location name
  results: PolicyImpactResults | null; // Policy impact results for calculations
}

const VehicleElectrificationPolicy: React.FC<VehicleElectrificationPolicyProps> = ({
  currentEVPercent, // 2045 BAU EV%
  currentEVPercent2023, // 2023 EV%
  targetEVPercent, // Slider value
  location,
  results,
}) => {
  // Constants used in calculations
  const EV_KWH_PER_MILE = 0.3; // Average EV efficiency
  const ICE_IMPROVEMENT_FACTOR = 0.95; // Future ICE efficiency improvement
  const BASELINE_GRID_INTENSITY = 0.2; // kg CO2/kWh - California 2023
  
  // Calculate EV contribution from results
  const evContribution = results?.contributionsByPolicy.evAdoption || 0;
  
  // Get baseline data from results
  const baselineVMT = results?.baseline.vmt || 0;
  const baselineLightDutyGHG = results?.baseline.lightDutyGHG || 0;
  const baselineGhgPerMile = baselineVMT > 0 ? baselineLightDutyGHG / baselineVMT : 0;
  
  // Estimated grid carbon intensity (would use actual from scenario.solarCapacityMultiplier in real calculation)
  const estimatedGridIntensity = BASELINE_GRID_INTENSITY; // kg CO2/kWh
  
  // Calculate emission factors
  const iceGhgPerMile = baselineGhgPerMile * ICE_IMPROVEMENT_FACTOR; // tons CO2/mile
  const evGhgPerMileKg = estimatedGridIntensity * EV_KWH_PER_MILE; // kg CO2/mile
  const evGhgPerMile = evGhgPerMileKg / 1000; // tons CO2/mile
  
  // Calculate fleet-weighted emission factor at target
  const icePercent = 100 - targetEVPercent;
  const fleetWeightedGhgPerMile = (icePercent / 100 * iceGhgPerMile) + (targetEVPercent / 100 * evGhgPerMile);

  return (
    <div className="vehicle-electrification-policy">
      <div className="policy-details">
        <div className="details-section">
          <h4>Policy Description</h4>
          <p>
            Increase the percentage of light-duty vehicles that are electric to reduce greenhouse gas emissions.
            Electric vehicles (EVs) produce zero direct tailpipe emissions and, when powered by the electricity grid,
            have lower lifecycle emissions than internal combustion engine (ICE) vehicles. As the electricity grid
            decarbonizes, EV emissions decrease further. This policy calculates GHG reductions from replacing ICE vehicles
            with EVs, accounting for the mix of vehicles in the fleet and the carbon intensity of the electricity grid.
          </p>
        </div>

        <div className="details-section">
          <h4>Critical Assumptions</h4>
          <div className="calculation-results">
            <div className="result-item">
              <span className="result-label">Current EV Percentage ({location}, 2023):</span>
              <span className="result-value">{(currentEVPercent2023 || 0).toFixed(2)}%</span>
            </div>
            <div className="result-item">
              <span className="result-label">EV Percentage in 2045 under BAU:</span>
              <span className="result-value">{currentEVPercent.toFixed(2)}%</span>
              <div className="assumption-description">
                This is the projected EV adoption rate in 2045 under Business-As-Usual (BAU) scenario from EMFAC2025.
                This value is used as the leftmost (minimum) position of the slider.
              </div>
            </div>
            <div className="result-item">
              <span className="result-label">EV Percentage under Selection (by 2045):</span>
              <span className="result-value">{targetEVPercent}%</span>
              <div className="assumption-description">
                This is the target EV adoption rate selected using the slider. Emissions scale linearly from BAU (at {currentEVPercent.toFixed(2)}%) to zero (at 100%).
              </div>
            </div>
            <div className="result-item">
              <span className="result-label">EV Efficiency:</span>
              <span className="result-value">{EV_KWH_PER_MILE} kWh/mile</span>
            </div>
            <div className="result-item">
              <span className="result-label">ICE Improvement Factor:</span>
              <span className="result-value">{ICE_IMPROVEMENT_FACTOR}</span>
              <div className="assumption-description">
                Assumes future ICE vehicles will be 5% more efficient than current baseline
              </div>
            </div>
            <div className="result-item">
              <span className="result-label">Baseline Grid Carbon Intensity:</span>
              <span className="result-value">{BASELINE_GRID_INTENSITY} kg CO₂/kWh</span>
              <div className="assumption-description">
                California 2023 grid carbon intensity. This decreases as Grid Decarbonization policy is implemented.
              </div>
            </div>
          </div>
        </div>

        <div className="details-section">
          <h4>Calculation Results</h4>
          <div className="calculation-results">
            <div className="result-item">
              <span className="result-label">Baseline Light-Duty VMT:</span>
              <span className="result-value">{baselineVMT.toLocaleString(undefined, {maximumFractionDigits: 0})} miles/year</span>
            </div>
            <div className="result-item">
              <span className="result-label">Baseline GHG per Mile (ICE):</span>
              <span className="result-value">{(baselineGhgPerMile * 1000).toFixed(4)} kg CO₂e/mile</span>
            </div>
            <div className="result-item">
              <span className="result-label">ICE GHG per Mile (2023 baseline):</span>
              <span className="result-value">{(iceGhgPerMile * 1000).toFixed(4)} kg CO₂e/mile</span>
            </div>
            <div className="result-item">
              <span className="result-label">EV GHG per Mile (grid-powered):</span>
              <span className="result-value">{(evGhgPerMile * 1000).toFixed(4)} kg CO₂e/mile</span>
            </div>
            <div className="result-item">
              <span className="result-label">Fleet-Weighted GHG per Mile (at {targetEVPercent}% EVs):</span>
              <span className="result-value">{(fleetWeightedGhgPerMile * 1000).toFixed(4)} kg CO₂e/mile</span>
            </div>
            <div className="result-item highlight">
              <span className="result-label">Net GHG Reduction:</span>
              <span className="result-value">{evContribution.toLocaleString(undefined, {maximumFractionDigits: 0})} MT CO₂e</span>
            </div>
          </div>
        </div>

        <div className="details-section">
          <h4>Methodology</h4>
          <p>
            The GHG reduction from vehicle electrification is calculated by comparing fleet-weighted emissions
            per mile at the target EV adoption rate versus the baseline fleet composition. The calculation accounts for:
          </p>
          <ul>
            <li>Current EV adoption rate in the selected location</li>
            <li>Target EV adoption rate by 2045 (user-selected)</li>
            <li>ICE vehicle emission factors from EMFAC data (adjusted for future efficiency improvements)</li>
            <li>EV emission factors based on grid carbon intensity and EV energy efficiency</li>
            <li>Fleet-weighted average emissions per mile</li>
            <li>Projected vehicle miles traveled (VMT)</li>
          </ul>
          <p>
            As the electricity grid decarbonizes (through the Grid Decarbonization policy), EV emissions per mile decrease,
            further reducing total GHG emissions from the transportation sector.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VehicleElectrificationPolicy;

